import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import Link from 'components/shared/link';
import styles from './styles.module.scss';
import Card from './components/Card';

const CardsList = ({
  cards,
  theme = null,
  title = undefined,
  titleTag = 'h2',
  imageFit = 'cover',
  textsAlligment = 'left',
  imageHeight = undefined,
  imageBoxBackground = null,
  description = undefined,
  columns = 3,
  buttonLabel = null,
  buttonLink = null,
  secondaryButtonLink = null,
  secondaryButtonLabel = null,
}) => {
  const isPhone = useMemo(() => buttonLink?.includes('tel:'), [buttonLink]);
  const isSecondaryButtonPhone = useMemo(
    () => secondaryButtonLink?.includes('tel:'),
    [secondaryButtonLink]
  );
  return (
    <section
      className={cn(styles.cardsWrapper, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      {(title || description) && (
        <div className={styles.header}>
          {title && (
            <Heading className={styles.title} tag={titleTag} size="xl">
              {title}
            </Heading>
          )}
          {description && (
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>
      )}
      <div className={styles.inner}>
        <div className="container">
          <div className={cn(styles.cardsGrid, styles[`columns-${columns}`])}>
            {cards?.map((card, index) => (
              <Card
                key={index}
                card={card}
                imageFit={imageFit}
                textsAlligment={textsAlligment}
                imageHeight={imageHeight}
                imageBoxBackground={imageBoxBackground}
              />
            ))}
          </div>
          {buttonLabel && buttonLink && (
            <div className={styles.btnBox}>
              {/* {buttonHint && <p className={styles.btnHint}>{buttonHint}</p>} */}
              <Button
                theme="accent-primary"
                to={buttonLink}
                className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
              >
                {isPhone && <PhoneIcon />}
                {buttonLabel}
                {!isPhone && <ArrowRightIcon />}
              </Button>
            </div>
          )}
          {secondaryButtonLink && secondaryButtonLabel && (
            <div className={styles.secondaryBtnBox}>
              <Link to={secondaryButtonLink} className={styles.secondaryBtn}>
                {isSecondaryButtonPhone && <PhoneIcon />}
                <span>{secondaryButtonLabel}</span>
                {!isSecondaryButtonPhone && <ArrowRightIcon />}
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

CardsList.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }).isRequired,
      title: PropTypes.string,
      description: PropTypes.string,
      buttonLabel: PropTypes.string,
      buttonLink: PropTypes.string,
    })
  ).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  theme: PropTypes.oneOf([null, 'light', 'dark']),
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  imageFit: PropTypes.oneOf(['cover', 'contain']),
  textsAlligment: PropTypes.oneOf(['left', 'center']),
  imageHeight: PropTypes.number,
  imageBoxBackground: PropTypes.string,
  columns: PropTypes.number,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  secondaryButtonLink: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
};

export default CardsList;
